import * as React from "react";

import { styled, alpha } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import Typography from "@mui/material/Typography";
import { Badge, Button, Menu, MenuItem } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { Fade } from "@mui/material";
import DashboardHeader from "../pages/DashboardHeader";
import { useAuth } from "../contexts/AuthContext";
import "./css/navbar.css";

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "25px",
  textTransform: "none",
  fontWeight: "600",
  padding: "8px 20px",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "6px 16px",
    fontSize: "0.875rem",
  },
}));

const StyledAppBar = styled(AppBar)(({ theme, isscrolled }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(231, 231, 231, 0.3)",
  transition: "all 0.3s ease",
  height: isscrolled === "true" ? "64px" : "auto",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.98)",
  },
}));

const StyledTab = styled(Tab)(({ theme, isscrolled }) => ({
  textTransform: "none",
  fontSize: isscrolled === "true" ? 13 : 14,
  fontWeight: 500,
  transition: "all 0.3s ease",
  borderRadius: "10px",
  margin: "0 1px",
  padding: isscrolled === "true" ? "6px 8px" : "6px 12px",
  minHeight: "32px",
  minWidth: isscrolled === "true" ? "60px" : "90px",
  color: "#666",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
  },
  "&.Mui-selected": {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    fontWeight: 600,
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  margin: "4px 8px",
  borderRadius: "10px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: "translateX(5px)",
  },
  "&.Mui-selected": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
    },
  },
}));

const CompactTabs = styled(Tabs)(({ isscrolled }) => ({
  minHeight: "32px",
  height: "32px",
  maxWidth: isscrolled === "true" ? "500px" : "100%",
  margin: "0 auto",
  "& .MuiTabs-flexContainer": {
    height: "32px",
    justifyContent: "center",
  },
  "& .MuiTabs-scroller": {
    overflow: "hidden !important",
  },
}));

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const navRef = React.useRef(null);
  const { user } = useAuth();
  const [isLogged, setLogged] = React.useState(0);
  const navigate = useNavigate();

  const notifications = [
    "A new Session of Course A for students in grades 7th to 10th will begin from August 2025.",
    "We are open to collaborate with schools, Investors, Colleges. Email us on official@yapassio.com for any type of collaboration.",
    "Aptitudes Testing Software will be available soon on Courses Page.",
    "Personalized Dashboard is under maintainence, it will start working soon.",
  ];
  const [notifMenu, setNotifMenu] = React.useState(null);
  const open = Boolean(notifMenu);

  const handleClick = (event) => {
    setNotifMenu(event.currentTarget);
  };

  const handleClose = () => {
    setNotifMenu(null);
  };

  React.useEffect(() => {
    if (user) {
      setLogged(1);
    }
    const handleScroll = () => {
      const currentScrollTop = window.scrollY;
      const shouldBeScrolled = currentScrollTop > 20;

      if (shouldBeScrolled !== isScrolled) {
        setIsScrolled(shouldBeScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isScrolled]);

  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1] || "home";
  const path = {
    home: "Home",
    // dashboard: "Dashboard",
    // courses: "Courses",
    intern: "Careers",
    contactus: "Contact Us",
    aboutus: "About",
    // iiithprep: "IIITH_prep",
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const UserIconButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: alpha("#444", 0.2),
    backdropFilter: "blur(8px)",
    border: "1px solid",
    borderColor: alpha("#FFFFFF", 0.3),
    transition: "all 0.3s ease",
    width: { xs: "32px", sm: "40px" },
    height: { xs: "32px", sm: "40px" },
    "&:hover": {
      backgroundColor: alpha("#444", 0.3),
      borderColor: alpha("#FFFFFF", 0.4),
      transform: "scale(1.05)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: { xs: "18px", sm: "20px" },
      color: alpha("#000000", 0.7),
    },
  }));

  const drawer = (
    <Box sx={{ width: 250, p: 2 }}>
      <List>
        {Object.entries(path).map(([key, value]) => (
          <StyledListItem
            button
            key={key}
            component={Link}
            to={`/${key}`}
            selected={lastSegment === key}
          >
            <ListItemText
              primary={value}
              sx={{
                "& .MuiListItemText-primary": {
                  fontWeight: lastSegment === key ? 600 : 400,
                },
              }}
            />
          </StyledListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box>
      <StyledAppBar
        ref={navRef}
        position="fixed"
        elevation={isScrolled ? 2 : 0}
        isscrolled={isScrolled.toString()}
        sx={{
          transition: "all 0.3s ease",
          boxShadow: isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none",
        }}
      >
        {isLogged ? (
          <DashboardHeader />
        ) : (
          <Toolbar
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              px: { xs: 1, sm: 2 },
              minHeight: isScrolled
                ? "64px !important"
                : { xs: "56px", sm: "64px" },
              height: isScrolled ? "64px" : "auto",
              transition: "all 0.3s ease",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  display: { sm: "none" },
                  color: "#000000",
                  padding: "8px",
                  "&:hover": {
                    backgroundColor: alpha("#000", 0.05),
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: "24px",
                    fontWeight: "bold",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                <img
                  src="/logo.png"
                  alt="Logo"
                  style={{
                    height: isScrolled ? "35px" : "40px",
                    maxWidth: "100%",
                    transition: "height 0.3s ease",
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                mx: 2,
                display: { xs: "none", sm: "flex" },
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                maxWidth: isScrolled ? "600px" : "none",
                transition: "max-width 0.3s ease",
              }}
            >
              {/* <Fade in={!isScrolled}>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "400px",
                    display: isScrolled ? "none" : "block",
                  }}
                >
                  <Button
                    sx={{
                      width: "100%",
                      height: "40px",
                      backgroundColor: "#f0f4f8",
                      border: "2px solid #e2e8f0",
                      borderRadius: "25px",
                      color: "#94a3b8",
                      textTransform: "none",
                      justifyContent: "flex-start",
                      pl: 3,
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                        borderColor: "#cbd5e1",
                      },
                    }}
                    startIcon={<SearchIcon />}
                  >
                    Search courses, internships...
                  </Button>
                </Box>
              </Fade> */}

              <Fade in={isScrolled}>
                <Box
                  sx={{
                    display: !isScrolled ? "none" : "flex",
                    alignItems: "center",
                    height: "32px",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <CompactTabs
                    value={Object.keys(path).indexOf(lastSegment)}
                    isscrolled={isScrolled.toString()}
                    sx={{
                      "& .MuiTabs-indicator": {
                        display: "none",
                      },
                    }}
                  >
                    {Object.entries(path).map(([key, value]) => (
                      <StyledTab
                        key={key}
                        component={Link}
                        to={`/${key}`}
                        label={value}
                        isscrolled={isScrolled.toString()}
                      />
                    ))}
                  </CompactTabs>
                </Box>
              </Fade>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: { xs: 0.5, sm: 2 },
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {/* <div style={{ position: "relative" }}>
                <IconButton
                  sx={{
                    color: "#30B0C7",
                    "&:hover": { backgroundColor: "#CAEAF0" },
                    transition: "0.3s",
                  }}
                  onClick={handleClick}
                >
                  <Badge color="error">
                    <NotificationsNoneIcon fontSize="medium" />
                  </Badge>
                </IconButton>

                {open && (
                  <div
                    onClick={handleClose} // Clicking outside closes the panel
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      background: "rgba(0, 0, 0, 0.3)", // Semi-transparent grey
                      zIndex: 99, // Below the panel but above the page content
                      transition: "background 1s ease-in-out",
                    }}
                  ></div>
                )}
                {open && (
                  <div
                    className="notification-panel"
                    style={{
                      display: "block",
                      padding: "20px 20px",
                      width: "470px",
                      maxWidth: "90vw",
                      maxHeight: "90vh",
                      backgroundColor: "white",
                      borderRadius: "20px",
                      zIndex: "100",
                      boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.25)",
                      fontFamily: "Inter",
                      overflowY: "auto",
                      animationName: "fadeInOut",
                      animationDuration: "0.12s",
                      animationIterationCount: "1",
                    }}
                    onClick={handleClose}
                  >
                    <p
                      style={{
                        color: "black",
                        fontWeight: "400",
                        fontSize: "24px",
                        color: "black",
                        letterSpacing: "0.05em",
                        marginBottom: "1rem",
                        padding: "0px 10px",
                      }}
                    >
                      IMPORTANT NOTIFICATIONS
                    </p>
                    {notifications.map((i, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "10px",
                          cursor: "pointer",
                          fontSize: "20px",
                          lineHeight: "24px",
                          fontWeight: "400",
                          borderBottom:
                            index !== notifications.length - 1
                              ? "1px solid rgba(0, 0, 0, 0.2)"
                              : "none",
                          transition: "background-color 0.2s ease-in-out",
                          borderRadius: "5px",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "rgba(68, 68, 68, 0.25)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                        }}
                      >
                        <span
                          style={{
                            color: "#49454F",
                            letterSpacing: "0.02em",
                          }}
                        >
                          {i}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <style>
                {`
                  @keyframes fadeInOut {
                    from { 
                      scale: 0% 1;
                      opacity: 0%;
                    }
                    to {
                      scale: 100% 1;
                      opacity: 100%;
                    }
                  }
                  .notification-panel {  
                      position:absolute;
                      top: 40px;
                      right: 0;
                      z-index:100;
                    }
                  @media (max-width: 768px) {
                    .notification-panel {
                      position:fixed;
                      right:auto;
                      top:50px;
                      left:25px;
                    }
                  }
                `}
              </style> */}

              {/* <Box
                onClick={() =>
                  navigate("/initial-info", {
                    state: { fromYapassioButton: true },
                  })
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  borderRadius: "10px",
                  cursor: "pointer",
                  px: { xs: 0, sm: 3 },
                  py: { xs: 0.2, sm: 2 },
                  minHeight: { xs: "25px", sm: "52px" }, // 👈 mobile tap target
                  transition: "all 0.2s ease",
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: { xs: "0px", sm: "16px" },
                    py: { xs: "0px", sm: "6px" },
                    px: { xs: "0px", sm: "3px" },
                    borderRadius: "8px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    backgroundColor: "#30B0C7",
                    "&:hover": {
                      backgroundColor: "#279DB2",
                    },
                  }}
                >
                  Go to Counselling
                </Typography>
                <UserIconButton
                  style={{ border: "none", backgroundColor: "#fff" }}
                >
                  <img
                    src="/AI_Addon/counselling-header-icon.png"
                    style={{ height: "32px", backgroundColor: "#fff" }}
                  />
                </UserIconButton>
              </Box>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  navigate("/AI/dashboard", {
                    state: { fromYapassioButton: true }, // 👈 Add this
                  })
                }
              >
                <span className="yapassio-text">Try Yapassio AI</span>

                <UserIconButton
                  style={{ border: "none", backgroundColor: "#fff" }}
                >
                  <img
                    src="/AI_Addon/ai-header-icon.png"
                    style={{ height: "32px", backgroundColor: "#fff" }}
                  />
                </UserIconButton>
              </div>

              <StyledButton
                variant="contained"
                component={Link}
                to="/login"
                sx={{
                  backgroundColor: "#30B0C7",
                  color: "white",
                  padding: isScrolled ? "6px 16px" : "8px 20px",
                  "&:hover": {
                    backgroundColor: "#2799AD",
                  },
                }}
              >
                Log In
              </StyledButton>
              <StyledButton
                variant="contained"
                component={Link}
                to="/signup"
                sx={{
                  border: "1px solid #30B0C7",
                  backgroundColor: "white",
                  color: "#30B0C7",
                  padding: isScrolled ? "6px 16px" : "8px 20px",
                  "&:hover": {
                    backgroundColor: "rgba(231, 231, 231, 0.3)",
                  },
                }}
              >
                Sign Up
              </StyledButton> */}
            </Box>
          </Toolbar>
        )}

        <Fade in={!isScrolled}>
          <Box
            sx={{
              display: { xs: "none", sm: "block" },
              borderTop: "1px solid rgba(231, 231, 231, 0.3)",
              py: 1,
            }}
          >
            <Tabs
              value={Object.keys(path).indexOf(lastSegment)}
              sx={{
                minHeight: "40px",
                "& .MuiTabs-indicator": {
                  display: "none",
                },
              }}
              centered
            >
              {Object.entries(path).map(([key, value]) => (
                <StyledTab
                  key={key}
                  component={Link}
                  to={`/${key}`}
                  label={value}
                  sx={{ fontSize: "16px" }}
                />
              ))}
            </Tabs>
          </Box>
        </Fade>
      </StyledAppBar>

      <Box
        sx={{
          height: isScrolled ? "64px" : { xs: "56px", sm: "112px" },
          transition: "height 0.3s ease",
        }}
      />

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 250,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
